import React from 'react';
import { Field, reduxForm } from 'redux-form';
import { Alert, Form, Button, Row, Col, FormGroup, Table, Panel, Label } from 'react-bootstrap';
import { FormActions, Static, FormFileUpload } from '../../common/components/formControls';
import { withTranslation } from 'react-i18next';
import ui from '../../common/services/ui';
import utils from '../../common/services/utils';
import { mapStatusToLabel } from './utils';
import { ConfirmationButton } from '../../common/components/formControls';
import BackButton from '../../common/components/BackButton';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import LabelWithTooltip from '../../common/components/LabelWithTooltip';
import { faCheck, faTimes, faDollarSign, faCircleInfo } from '@fortawesome/free-solid-svg-icons';
import { faEnvelope, faMoneyBill1 } from '@fortawesome/free-regular-svg-icons';
import InstalmentList from './InstalmentList';
import moment from 'moment';
import Enrolments from '../enrolments/List';
import SubscriptionPanel, { isSubscriptionRunning } from './SubscriptionPanel';

const SettingsForm = ({ payment, error, handleSubmit, pristine, submitting, isReadonly, submitSucceeded, initialValues,
	canMarkAsPaid, canCancel, markAsPaid, markAsCancelled, canMarkAsNotApproved, markAsNotApproved, canUploadInvoice,
	canRefundUnbookedAnnual, refundUnbookedAnnual, canSendInvoice, sendInvoice, canApplyPromoCode, onApplyPromoCode,
	canAddInstalment, addInstalment, canRetryInstalment, retryInstalment, markInstalmentAsPaid, canMarkInstalmentAsPaid, onRefresh,
	canManageSubscription, canViewSentEmails, recordSubscriptionCancellation, withdrawSubscriptionCancellation, t }) => {

	const documentData = initialValues && initialValues.invoice && {
		filename: initialValues.invoice.friendlyFilename,
		url: initialValues.invoice.url
	};

	const gradeRow = initialValues && <div>
		{initialValues.name || initialValues.grade} ({t(ui.days[initialValues.dayOfWeek])} {ui.formatTime(utils.parseTime(initialValues.startTime))})
		{initialValues.name && initialValues.grade && <div className='text-muted'>{initialValues.grade}</div>}
	</div>;
	const invoiceUrl = initialValues && initialValues.invoice && initialValues.invoice.url;

	const isInactive = initialValues && initialValues.isInactive;
	const isInstalmentPayment = initialValues && initialValues.isInstalmentPayment;
	const hasSubscriptionTransaction = initialValues && initialValues.subscriptionTransaction;
	const paymentMethodName = (initialValues && initialValues.paymentMethod && initialValues.paymentMethod.name) || '';
	const isGoCardlessPayment = paymentMethodName === 'GoCardless';
	const isVoucherPayment = paymentMethodName === 'Voucher';
	const parentId = initialValues && initialValues.parent.id;
	const childId = initialValues && initialValues.child.id;
	const paymentOption = initialValues && initialValues.paymentOption && `(${initialValues.paymentOption})`;

	const campaignTags = initialValues && initialValues.campaignTags;
	const renderCampaignTags = campaignTags && Object.keys(campaignTags).map(key => <div key={key}>{key}: <b style={{ color: '#707070' }}>{campaignTags[key]}</b></div>);
	const enrolments = initialValues && initialValues.enrolments;
	const subscriptionRunning = isSubscriptionRunning(payment.subscription);

	return (
		<Form horizontal onSubmit={handleSubmit}>
			{error && <Alert bsStyle='danger'>{error}</Alert>}
			{submitSucceeded && pristine && <Alert bsStyle='success'>{t('Common_UpdateSuccess')}</Alert>}
			{initialValues.isPaymentError && !isInactive && <Alert bsStyle='danger'>{initialValues.cancelationReason}</Alert>}
			{initialValues.automaticNotApprovedCancellationDate && <Alert bsStyle='warning'>
				{t('PaymentHistory_AutomaticCancellationWarning', { cancellationDate: ui.formatDateTime(initialValues.automaticNotApprovedCancellationDate) })}
			</Alert>}
			<Row>
				<Col sm={6}>
					<Panel>
						<Panel.Heading>{t('PaymentHistory_Order')}</Panel.Heading>
						<Panel.Body>
							<Field name='id' component={Static} labelSize={4} horizontal label={t('PaymentHistory_Id')} />
							<Field name='created' component={Static} labelSize={4} horizontal formatType='dateTime' label={t('PaymentHistory_CreatedDate')} />
							<Field name='status' component={Static} labelSize={4} horizontal label={t('PaymentHistory_Status')} valueWrapper={value => <span>
								<Label bsStyle={mapStatusToLabel(value.id)}>{t(`PaymentStatusType_${value.name}`)}</Label>
								{isInactive && <span> <Label bsStyle='inverse'>{t('PaymentStatus_Inactive')}</Label></span>}
							</span>} />
							<Field name='paymentDate' component={Static} labelSize={4} formatType='dateTime' horizontal label={t('PaymentHistory_PaymentDate')} />
						</Panel.Body>
					</Panel>
				</Col>
				<Col sm={6}>
					<Panel>
						<Panel.Heading>{t('PaymentHistory_Parent')}</Panel.Heading>
						<Panel.Body>
							<Field name='parent.fullname' component={Static} labelSize={4} horizontal label={t('PaymentHistory_Parent')} valueWrapper={value => <Link to={`/parents/${parentId}`}>{value}</Link>} />
							<Field name='parent.email' component={Static} labelSize={4} horizontal label={t('Parent_Email')} />
							<Field name='parent.phoneNumber' component={Static} labelSize={4} horizontal label={t('PaymentHistory_PhoneNumber')} />
							<Field name='child.fullname' component={Static} labelSize={4} horizontal label={t('PaymentHistory_Child')} valueWrapper={value => <Link to={`/parents/${parentId}/children/${childId}`}>{value}</Link>} />
						</Panel.Body>
					</Panel>
				</Col>
			</Row>
			<Row>
				<Col sm={6}>
					<Panel>
						<Panel.Heading>{t('PaymentHistory_Enrolment')}</Panel.Heading>
						<Panel.Body>
							<Field name='center' component={Static} labelSize={4} horizontal label={t('PaymentHistory_Center')} />
							<Field name='grade' component={Static} labelSize={4} horizontal label={t('PaymentHistory_Grade')} valueWrapper={value => <span>{gradeRow}</span>} />
							<Field name='startDate' component={Static} labelSize={4} horizontal formatType='date' label={t('PaymentHistory_StartDate')} />
							<Field name='paymentType.name' component={Static} labelSize={4} horizontal label={t('PaymentHistory_PaymentType')} valueWrapper={value => <span>{t(`PaymentTypeEnum_${value}`)}</span>} />
						</Panel.Body>
					</Panel>
				</Col>
				<Col sm={6}>
					<Panel>
						<Panel.Heading>{t('PaymentHistory_PaymentMethod')}</Panel.Heading>
						<Panel.Body>
							<Field name='paymentMethod.name' component={Static} labelSize={4} horizontal label={t('PaymentHistory_PaymentMethod')} valueWrapper={value => `${value} ${paymentOption || ''}`} />
							{initialValues.paymentCode && <Field name='paymentCode' component={Static} labelSize={4} horizontal label={t('PaymentHistory_PaymnetCode')} />}
							<Field name='transaction' component={Static} labelSize={4} horizontal label={t('PaymentHistory_Transaction')} />
							{isVoucherPayment && <Field name='voucherSchemeName' component={Static} labelSize={4} horizontal label={t('PaymentHistory_VoucherSchemeName')} />}
							{isVoucherPayment && <Field name='voucherSchemeReference' component={Static} labelSize={4} horizontal label={t('PaymentHistory_VoucherSchemeReference')} />}
							{isGoCardlessPayment && <Field name='authorizationToken' component={Static} labelSize={4} horizontal label={t('PaymentHistory_Mandate')} />}
							{isInstalmentPayment && hasSubscriptionTransaction && <Field name='subscriptionTransaction' component={Static} labelSize={4} horizontal label={t(`PaymentHistory_${paymentMethodName}_Subscription`)} />}
						</Panel.Body>
					</Panel>
				</Col>
			</Row>
			<Row>
				<Col sm={6}>
					<Panel>
						<Panel.Heading>{t('PaymentHistory_Amounts')}</Panel.Heading>
						<Panel.Body>
							<Table>
								<thead>
									<tr>
										<th>{t('PaymentHistory_Price')}</th>
										<th>{t('PaymentHistory_NumberOfHours')}</th>
										<th>{t('PaymentHistory_Subtotal')}</th>
									</tr>
								</thead>
								<tbody>
									<tr>
										<td>{ui.formatAmount(payment.price)}</td>
										<td>{payment.quantity}</td>
										<td>{ui.formatAmount(payment.subtotal)}</td>
									</tr>
								</tbody>
								<tfoot>
									<tr><th colSpan={2}>{t('PaymentHistory_PaymentMethodFee')}</th><td>{ui.formatAmount(payment.paymentMethodFee)}</td></tr>
									<tr><th colSpan={2}><div>{t('PaymentHistory_Discount')}</div><div style={{ fontWeight: 'normal', color: '#000' }}>{payment.discountText}</div></th><td>{ui.formatAmount(payment.discount)}</td></tr>
									<tr><th colSpan={2}>{t('PaymentHistory_Total')}</th><td>{ui.formatAmount(payment.total)}</td></tr>
									{payment.refundedTotal && Number(payment.refundedTotal) > 0 ? <tr>
										<th>{t('PaymentHistory_TotalRefunded')}</th>
										<td>{Number(payment.refundedQuantity) > 0 ? payment.refundedQuantity : ''}</td>
										<td>{ui.formatAmount(payment.refundedTotal)}</td>
									</tr> : null}
									{payment.isInstalmentPayment && !payment.termPaymentFromAnnual ? <tr>
										<th colSpan={2}><LabelWithTooltip id='paidTotalTooltip' tooltip={t('PaymentHistory_PaidTotalTooltip')}>{t('PaymentHistory_PaidTotal')} <FontAwesomeIcon icon={faCircleInfo} className='text-muted' style={{ fontSize: '14px', marginLeft: '4px' }} /></LabelWithTooltip></th>
										<td>{ui.formatAmount(payment.paidTotal)}</td>
									</tr> : null}
									{payment.isInstalmentPayment && !payment.termPaymentFromAnnual ? <tr>
										<th colSpan={2}>{t('PaymentHistory_OutstandingTotal')}</th>
										<td>{ui.formatAmount(payment.outstandingTotal)}</td>
									</tr> : null}
									{payment.cancellationLessonsBooked !== undefined ? <tr>
										<th>{t('PaymentHistory_LessonsBooked')}</th>
										<td>{payment.cancellationLessonsBooked}</td>
										<td>
											{ui.formatAmount(payment.cancellationLessonsBookedAmount)}
											<span className={`badge ${Number(payment.cancellationValueFullTerm) >= 0 ? 'badge-success' : 'badge-warning'}`} style={{ marginLeft: '8px' }}>
												{Number(payment.cancellationValueFullTerm) > 0 ? '+' : ''}{ui.formatAmount(payment.cancellationValueFullTerm)}
											</span>
										</td>
									</tr> : null}
									{payment.cancellationLessonsPast !== undefined ? <tr>
										<th>{t('PaymentHistory_LessonsCompleted')}</th>
										<td>{payment.cancellationLessonsPast}</td>
										<td>
											{ui.formatAmount(payment.cancellationLessonsCompletedAmount)}
											<span className={`badge ${Number(payment.cancellationValueAsOfToday) >= 0 ? 'badge-success' : 'badge-warning'}`} style={{ marginLeft: '8px' }}>
												{Number(payment.cancellationValueAsOfToday) > 0 ? '+' : ''}{ui.formatAmount(payment.cancellationValueAsOfToday)}
											</span>
										</td>
									</tr> : null}
								</tfoot>
							</Table>
						</Panel.Body>
					</Panel>
				</Col>
				<Col sm={6}>
					<Panel>
						<Panel.Heading>{t('PaymentHistory_Notes')}</Panel.Heading>
						<Panel.Body>
							{campaignTags && <Field name='campaignTags' component={Static} labelSize={4} horizontal label={t('PaymentHistory_Campaign')} valueWrapper={value => <span>{renderCampaignTags}</span>} />}
							<Field name='notes' component={Static} labelSize={0} formatType='html' horizontal />
						</Panel.Body>
					</Panel>
				</Col>
			</Row>
			{payment.subscription && <SubscriptionPanel subscription={payment.subscription} paymentId={payment.id} canManage={canManageSubscription} canViewSentEmails={canViewSentEmails}
				canEndNow={canCancel} onRecordCancellation={recordSubscriptionCancellation} onWithdrawCancellation={withdrawSubscriptionCancellation} onEndNow={markAsCancelled} />}
			{isInstalmentPayment && !payment.termPaymentFromAnnual && <InstalmentList instalments={payment.instalments.sort((a, b) => moment(a.chargeDate) - moment(b.chargeDate))} instalmentTemplateName={payment.instalmentTemplateName} instalmentPlan={payment.instalmentPlan} isInactive={isInactive}
				canRetryInstalment={canRetryInstalment} retryInstalment={retryInstalment} paymentMethod={payment.paymentMethod} onRefresh={onRefresh} markInstalmentAsPaid={markInstalmentAsPaid} canMarkInstalmentAsPaid={canMarkInstalmentAsPaid} />}
			{enrolments && enrolments.length > 0 &&
				<Panel>
					<Panel.Heading>{t('PaymentHistory_Enrolments')}</Panel.Heading>
					<Panel.Body>
						<Enrolments isReadonly={isReadonly} data={enrolments} parentId={parentId}
							requireAddress={false} requirePostcode={false} requireChildSchool={false} onlyEnrolmentData />
					</Panel.Body>
				</Panel>}
			{canUploadInvoice && <Field name='invoiceId' component={FormFileUpload} disabled={isReadonly} horizontal label={t('PaymentHistory_Invoice')} initialData={documentData} />}
			{!canUploadInvoice && invoiceUrl && <FormGroup>
				<Col sm={10} smOffset={2}>
					<a target='blank' href={invoiceUrl}>{t('PaymentHistory_ViewInvoice')}</a>
				</Col>
			</FormGroup>}

			<FormActions horizontal>
				<BackButton />
				<Button type='submit' disabled={isReadonly || pristine || submitting} bsStyle='primary'>{t('Common_Save')}</Button>
				<div className='pull-right'>
					{canMarkAsNotApproved && <Button onClick={markAsNotApproved} bsStyle='warning' style={{ marginRight: '5px' }}><FontAwesomeIcon icon={faMoneyBill1} /> {t('PaymentHistory_MarkAsNotApproved')}</Button>}
					{canMarkAsPaid && <Button onClick={markAsPaid} bsStyle='primary' style={{ marginRight: '5px' }}><FontAwesomeIcon icon={faCheck} /> {t('PaymentHistory_MarkAsPaid')}</Button>}
					{canAddInstalment && <Button onClick={addInstalment} bsStyle='primary' style={{ marginRight: '5px' }}><FontAwesomeIcon icon={faCheck} /> {t('PaymentHistory_AddInstalment')}</Button>}
					{canCancel && !subscriptionRunning && <Button onClick={markAsCancelled} bsStyle='danger' style={{ marginRight: '5px' }}><FontAwesomeIcon icon={faTimes} /> {t('PaymentHistory_MarkAsCancelled')}</Button>}
					{canRefundUnbookedAnnual && <Button onClick={refundUnbookedAnnual} bsStyle='danger' style={{ marginRight: '5px' }}><FontAwesomeIcon icon={faTimes} /> {t('PaymentHistory_RefundUnbookedAnnual')}</Button>}
					{canSendInvoice && <ConfirmationButton
						onConfirmation={sendInvoice}
						confirmationTitle={t('PaymentHistory_SendInvoiceTitle')}
						confirmationMessage={t('PaymentHistory_SendInvoiceConfirmationMessage')}
						displayType={'inline'}
						bsStyle='warning'
						icon={faEnvelope}
						btnClasses='ml-05'
						label={t('PaymentHistory_SendInvoice')} />
					}
					{canApplyPromoCode && <Button onClick={onApplyPromoCode} bsStyle='warning'><FontAwesomeIcon icon={faDollarSign} /> {t('PaymentHistory_PromoCode_Add')}</Button>}
				</div>
			</FormActions>
		</Form>
	);
};

export default reduxForm({ form: 'paymentHistory', enableReinitialize: true })(withTranslation()(SettingsForm));
